import {
    APIKeyError,
    KNOWN_APIKEY_CODES,
    PASSTHROUGH_PROVIDERS,
    type APIKey,
    type APIKeyCreated,
    type APIKeyErrorCode,
    type APIKeyMode,
    type CreateAPIKeyInput,
    type PassthroughProvider,
    type RoutingEntry,
    type RoutingMap,
    type RoutingStrategyMap,
} from '@/domain/api-keys'
import type { Tier } from '@/domain/tiers'
import type { APIKeysApi } from '@/ports/apikeys-api'

import { appendActiveTenant, applyActiveTenantToBody } from './_active-tenant'

/**
 * HttpAPIKeysApi : adapter fetch des endpoints /api/v1/apikeys.
 * GET pour list, POST pour create (secret retourné UNE FOIS), DELETE
 * pour revoke. Les mutations requièrent X-CSRF-Token.
 */
export class HttpAPIKeysApi implements APIKeysApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async list(): Promise<APIKey[]> {
        const params = appendActiveTenant(new URLSearchParams())
        const query = params.toString()
        const url = query ? `/api/v1/apikeys?${query}` : '/api/v1/apikeys'
        const resp = await this.send(url, { method: 'GET' })
        if (!resp.ok) {
            throw await readAPIKeyError(resp)
        }
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new APIKeyError('internal_error', 'Invalid /apikeys response shape')
        }
        return body.items.map(parseAPIKey)
    }

    async create(input: CreateAPIKeyInput, csrfToken: string): Promise<APIKeyCreated> {
        const base: Record<string, unknown> = {
            name: input.name,
        }
        if (input.environment) {
            base.environment = input.environment
        }
        if (input.tenantId) {
            base.tenant_id = input.tenantId
        }
        if (input.userId) {
            base.user_id = input.userId
        }
        if (input.routing) {
            base.routing = serializeRouting(input.routing)
        }
        if (input.routingStrategies && Object.keys(input.routingStrategies).length > 0) {
            base.routing_strategies = serializeStrategies(input.routingStrategies)
        }
        if (input.mode) {
            base.mode = input.mode
        }
        if (input.passthroughProvider) {
            base.passthrough_provider = input.passthroughProvider
        }
        const payload = applyActiveTenantToBody(base)
        const resp = await this.send('/api/v1/apikeys', {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) {
            throw await readAPIKeyError(resp)
        }
        const body = (await resp.json()) as { apikey?: unknown; secret?: unknown }
        if (typeof body.secret !== 'string' || !body.secret) {
            throw new APIKeyError('internal_error', 'Server did not return a secret value')
        }
        return {
            apikey: parseAPIKey(body.apikey),
            secret: body.secret,
        }
    }

    async revoke(id: string, csrfToken: string): Promise<void> {
        const resp = await this.send(`/api/v1/apikeys/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: csrfHeaders(csrfToken),
        })
        if (resp.status === 204) {
            return
        }
        throw await readAPIKeyError(resp)
    }

    async setTeam(id: string, teamId: string, csrfToken: string): Promise<APIKey> {
        const resp = await this.send(`/api/v1/apikeys/${encodeURIComponent(id)}/team`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ team_id: teamId }),
        })
        if (!resp.ok) {
            throw await readAPIKeyError(resp)
        }
        const body = (await resp.json()) as { apikey?: unknown }
        return parseAPIKey(body.apikey)
    }

    async setProjectTag(id: string, projectTag: string, csrfToken: string): Promise<APIKey> {
        const resp = await this.send(`/api/v1/apikeys/${encodeURIComponent(id)}/project-tag`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ project_tag: projectTag }),
        })
        if (!resp.ok) {
            throw await readAPIKeyError(resp)
        }
        const body = (await resp.json()) as { apikey?: unknown }
        return parseAPIKey(body.apikey)
    }

    async setRoute(id: string, routeId: string, csrfToken: string): Promise<APIKey> {
        const resp = await this.send(`/api/v1/apikeys/${encodeURIComponent(id)}/route`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ route_id: routeId }),
        })
        if (!resp.ok) {
            throw await readAPIKeyError(resp)
        }
        const body = (await resp.json()) as { apikey?: unknown }
        return parseAPIKey(body.apikey)
    }

    private send(path: string, init: RequestInit): Promise<Response> {
        return fetch(this.baseURL + path, {
            ...init,
            credentials: 'include',
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new APIKeyError('network_error', message)
        })
    }
}

function csrfHeaders(token: string): Record<string, string> {
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
}

function parseAPIKey(raw: unknown): APIKey {
    if (!raw || typeof raw !== 'object') {
        throw new APIKeyError('internal_error', 'Invalid apikey payload')
    }
    const obj = raw as Record<string, unknown>
    const env = obj.environment === 'test' ? 'test' : 'live'
    const scopesRaw = (obj.scopes as Record<string, unknown> | undefined) ?? {}
    return {
        id: stringField(obj.id),
        tenantId: stringField(obj.tenant_id),
        createdByUserId: stringField(obj.created_by_user_id),
        userId: stringField(obj.user_id) || undefined,
        teamId: stringField(obj.team_id) || undefined,
        projectTag: stringField(obj.project_tag) || undefined,
        routeId: stringField(obj.route_id) || undefined,
        routing: parseRouting(obj.routing),
        routingStrategies: parseStrategies(obj.routing_strategies),
        mode: parseMode(obj.mode),
        passthroughProvider: parsePassthroughProvider(obj.passthrough_provider),
        name: stringField(obj.name),
        prefix: stringField(obj.prefix),
        environment: env,
        revoked: typeof obj.revoked === 'boolean' ? obj.revoked : false,
        createdAt: stringField(obj.created_at),
        lastUsedAt: stringField(obj.last_used_at) || undefined,
        scopes: {
            models: stringArray(scopesRaw.models),
            routes: stringArray(scopesRaw.routes),
            budgetDailyEUR: numberField(scopesRaw.budget_daily_eur),
            budgetMonthlyEUR: numberField(scopesRaw.budget_monthly_eur),
            rateLimitRPM: numberField(scopesRaw.rate_limit_rpm),
            rateLimitTPM: numberField(scopesRaw.rate_limit_tpm),
            piiModesAllowed: stringArray(scopesRaw.pii_modes_allowed),
            ipAllowlist: stringArray(scopesRaw.ip_allowlist),
            expiresAt: stringField(scopesRaw.expires_at) || undefined,
        },
    }
}

function parseMode(raw: unknown): APIKeyMode {
    return raw === 'passthrough' ? 'passthrough' : 'api_key'
}

function parsePassthroughProvider(raw: unknown): PassthroughProvider | undefined {
    if (typeof raw !== 'string' || raw === '') return undefined
    return (PASSTHROUGH_PROVIDERS as readonly string[]).includes(raw)
        ? (raw as PassthroughProvider)
        : undefined
}

function parseRouting(raw: unknown): RoutingMap {
    if (!raw || typeof raw !== 'object') {
        return {}
    }
    const out: Record<string, RoutingEntry[]> = {}
    for (const [tier, entries] of Object.entries(raw as Record<string, unknown>)) {
        if (!Array.isArray(entries)) continue
        out[tier] = entries
            .map((e) => {
                const entry = e as Record<string, unknown>
                const re: { providerKeyId: string; model: string; weight?: number } = {
                    providerKeyId: stringField(entry.provider_key_id),
                    model: stringField(entry.model),
                }
                if (typeof entry.weight === 'number' && entry.weight > 0) re.weight = entry.weight
                return re
            })
            .filter((e) => e.providerKeyId && e.model)
    }
    return out as RoutingMap
}

function parseStrategies(raw: unknown): RoutingStrategyMap | undefined {
    if (!raw || typeof raw !== 'object') return undefined
    const out: Record<string, string> = {}
    for (const [tier, strategy] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof strategy === 'string' && strategy) out[tier] = strategy
    }
    return Object.keys(out).length > 0 ? (out as RoutingStrategyMap) : undefined
}

type RoutingEntryWire = { provider_key_id: string; model: string; weight?: number }

function serializeRouting(routing: RoutingMap): Record<string, RoutingEntryWire[]> {
    const out: Record<string, RoutingEntryWire[]> = {}
    for (const tier of Object.keys(routing) as Tier[]) {
        const cascade = routing[tier]
        if (!cascade || cascade.length === 0) continue
        out[tier] = cascade.map((e) => {
            const wire: RoutingEntryWire = { provider_key_id: e.providerKeyId, model: e.model }
            if (e.weight && e.weight > 0) wire.weight = e.weight
            return wire
        })
    }
    return out
}

function serializeStrategies(strategies: RoutingStrategyMap): Record<string, string> {
    const out: Record<string, string> = {}
    for (const [tier, strategy] of Object.entries(strategies)) {
        if (typeof strategy === 'string' && strategy) out[tier] = strategy
    }
    return out
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

function numberField(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function stringArray(v: unknown): string[] {
    if (!Array.isArray(v)) {
        return []
    }
    return v.filter((x): x is string => typeof x === 'string')
}

async function readAPIKeyError(resp: Response): Promise<APIKeyError> {
    if (resp.status === 401) {
        return new APIKeyError('unauthorized', 'Session expired', 401)
    }
    if (resp.status === 403) {
        try {
            const body = (await resp.json()) as { error?: { code?: unknown } }
            const code = typeof body.error?.code === 'string' ? body.error.code : ''
            if (code === 'csrf_required' || code === 'csrf_invalid' || code === 'tenant_scope') {
                return new APIKeyError(code as APIKeyErrorCode, code, 403)
            }
        } catch {
            // fallback
        }
        return new APIKeyError('forbidden', 'Forbidden', 403)
    }
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        return new APIKeyError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new APIKeyError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: APIKeyErrorCode = (KNOWN_APIKEY_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as APIKeyErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new APIKeyError(code, message, resp.status)
}
